import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import ReactCountryFlag from 'react-country-flag';
import { searchPlayers } from '../../services/api';
import { Player } from '../../models/Player';
import convertCountryCodeToAlpha2 from '../../utils/countryCodeConverter';

const PlayerSearch: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const results = await searchPlayers(searchTerm);
      setPlayers(results);
    } catch (err) {
      console.error('Error searching for players:', err);
      setError('Failed to search for players. Please try again.');
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4">Player Search</h2>

      <Form onSubmit={handleSearch} className="mb-4">
        <Row>
          <Col md={8}>
            <Form.Group>
              <Form.Control
                type="text"
                placeholder="Search by player name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Col>
        </Row>
      </Form>

      {error && <div className="alert alert-danger">{error}</div>}

      {players.length > 0 ? (
        <ListGroup>
          {players.map((player) => (
            <ListGroup.Item key={player.id} className="py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ textAlign: 'left', width: '60%' }}>
                  <div className="d-flex align-items-center">
                    {player.nationality && (
                      <span className="me-2" title={player.nationality}>
                        <ReactCountryFlag
                          countryCode={convertCountryCodeToAlpha2(player.nationality)}
                          svg
                          style={{ width: '1.2em', height: '1.2em', paddingTop: '0px' }}
                        />
                      </span>
                    )}
                    <Link to={`/players/${player.id}`} className="text-decoration-none">
                      <div className="fw-bold" style={{ textAlign: 'left' }}>{player.name}</div>
                    </Link>
                    {player.ageRange && (
                      <Badge bg="secondary" className="ms-2 py-1 px-2" style={{ fontSize: '0.7rem' }}>
                        {player.ageRange}
                      </Badge>
                    )}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    {player.isPro && <Badge bg="success" className="py-1 px-2" style={{ fontSize: '0.7rem' }}>Pro</Badge>}
                  </div>
                </div>

                {player.ratings.length > 0 && (
                  <div className="d-flex align-items-center ms-2">
                    <div className="text-center me-2">
                      <div className="small text-muted mb-1">Singles</div>
                      <Badge bg="light" text="dark" className="py-1 px-2">
                        {player.ratings[0].utr?.singles?.toFixed(2) || '-'}
                      </Badge>
                    </div>
                    <div className="text-center">
                      <div className="small text-muted mb-1">Doubles</div>
                      <Badge bg="light" text="dark" className="py-1 px-2">
                        {player.ratings[0].utr?.doubles?.toFixed(2) || '-'}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        !loading && <p>No players found. Try a different search term.</p>
      )}
    </div>
  );
};

export default PlayerSearch;
